import React, { useState, useEffect } from "react";
import AuthContext from "../Authentication/AuthContext";
import {
  Modal,
  Typography,
  Box as MuiBox,
  Table,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
} from "@mui/material";
import { userUploadFile, listOpenAiVectorStoreFiles, deleteVectorStoreFileById, deleteFileById, searchByFileName } from "../api-service";
import FileSearchBar from "./FileSearchBar";
import FileTableRows from "./FileTableRows";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import CustomSnackbar from "./CustomSnackbar";
import UploadButton from "./UploadButton";
import { formatFileSize, showSnackbar } from "./Utils";

function FileManagementModal({ open, onClose, token }) {
  const { userType } = React.useContext(AuthContext);
  const [files, setFiles] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (open) {
      setLoading(true);
      listOpenAiVectorStoreFiles(token)
        .then(setFiles)
        .catch(error => {
          console.error("Error fetching files:", error);
          showSnackbar(setSnackbar, "Failed to load files. Please try again.", error);
        })
        .finally(() => setLoading(false));
    }
  }, [open, token]);

  useEffect(() => {
  if (!searchQuery.trim()) {
    setSearchResults([]);
    setPage(0);
  }
}, [searchQuery]);

  const filteredFiles = searchResults.length > 0 ? searchResults : files;
  const paginatedFiles = filteredFiles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploading(true);
      try {
        await userUploadFile(file, token);
        const updatedFiles = await listOpenAiVectorStoreFiles(token);
        setFiles(updatedFiles);
        showSnackbar(setSnackbar, `${file.name} added successfully.`, "success");

      } catch (err) {
        showSnackbar(setSnackbar, `Failed to add ${file.name}. Please try again.`, "error");
        console.error("Error uploading file:", err);

      } finally {
        setUploading(false);
      }
    }
  };

  const handleDeleteClick = (file) => {
    setFileToDelete(file);
    setConfirmOpen(true);
  };
  
  const handleConfirmDelete = async () => {
    try {
      if (!fileToDelete) return;

      setDeleting(true);
      await deleteVectorStoreFileById(fileToDelete.Id, token);
      await deleteFileById(fileToDelete.Id, token);
      showSnackbar(setSnackbar, `${fileToDelete.Filename} deleted successfully.`, "success");
      const updatedFiles = await listOpenAiVectorStoreFiles(token);
      setFiles(updatedFiles);

    } catch (err) {
      showSnackbar(setSnackbar, `Failed to delete ${fileToDelete.Filename}. Please try again.`, "error");
      console.error("Error uploading file:", err);

    } finally {
      setFileToDelete(null);
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  return (
  <Modal open={open} onClose={onClose}>
    <MuiBox
      sx={{
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width: "85%",
        height: "80%",
        bgcolor: "background.paper",
        borderRadius: "25px",
        boxShadow: 24,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <MuiBox sx={{ px: 4, pt: 4 }}>
        <MuiBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            id="file-management-modal-title"
            variant="h4"
            component="h4"
            sx={{ color: "#392545", fontWeight: "550" }}
          >
            Files
          </Typography>

          {userType === "Admin" && (
            <UploadButton uploading={uploading} onFileSelect={handleFileUpload} />
          )}
        </MuiBox>

        <FileSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          loading={searchLoading}
          onSearch={async () => {
            if (!searchQuery.trim()) return;

            setSearchLoading(true);
            try {
              const result = await searchByFileName(token, searchQuery);
              setSearchResults(result);

              if (result.length === 0) {
                showSnackbar(setSnackbar, "No files found.", "info");
              }
            } catch (err) {
              console.error("Search failed:", err);
              showSnackbar(setSnackbar, "Search failed. Please try again.", "error");
            } finally {
              setSearchLoading(false);
            }
          }}
        />
      </MuiBox>

      <MuiBox
        sx={{
          px: 4,
          pt: 2,
          pb: 2,
          overflowY: "auto",
          flexGrow: 1,
        }}
      >
        {loading || searchLoading ? (
          <MuiBox
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "80%",
              minHeight: 200,
            }}
          >
            <CircularProgress />
          </MuiBox>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                mt: 2,
                borderRadius: "25px",
                boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: "50%", fontWeight: "700" }}>File Name</TableCell>
                    <TableCell sx={{ width: "30%", fontWeight: "700" }}>Uploaded At</TableCell>
                    <TableCell sx={{ width: "10%", fontWeight: "700" }}>Size</TableCell>
                    {userType === "Admin" && (
                      <TableCell sx={{ width: "10%", fontWeight: "700" }} align="right">
                        Delete
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <FileTableRows
                  files={paginatedFiles}
                  onDeleteClick={handleDeleteClick}
                  formatFileSize={formatFileSize}
                  canDelete={userType === "Admin"}
                />
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredFiles.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25]}
              sx={{ mt: 1 }}
            />
          </>
        )}

        <DeleteConfirmationDialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={handleConfirmDelete}
          file={fileToDelete}
          deleting={deleting}
        />

        <CustomSnackbar
          open={snackbar.open}
          message={snackbar.message}
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        />
      </MuiBox>
    </MuiBox>
  </Modal>
);
}

export default FileManagementModal;